import React from 'react'

export interface DataChipProps
{
    value: React.ReactNode
    color?: 'emerald' | 'rose' | 'slate' | 'amber' | 'green' | 'red' | 'neutral'
    prefix?: React.ReactNode
    suffix?: React.ReactNode
    showArrow?: boolean
    className?: string
    size?: 'sm' | 'md'
    fastPathId?: string
}

export const DataChip: React.FC<DataChipProps> = React.memo( ( {
    value,
    color = 'slate',
    prefix,
    suffix,
    showArrow = false,
    className = '',
    size = 'md',
    fastPathId,
} ) =>
{
    // Normalize logic
    let isPos = false;
    let isNeg = false;

    if ( typeof value === 'number' ) {
        isPos = value > 0;
        isNeg = value < 0;
    } else if ( typeof value === 'string' ) {
        const parsed = parseFloat( value.replace( /,/g, '' ) );
        if ( !isNaN( parsed ) ) {
            isPos = parsed > 0;
            isNeg = parsed < 0;
        }
    }

    // Map 'green' -> 'emerald', 'red' -> 'rose', 'neutral' -> 'slate'
    let themeColor = color;
    if ( color === 'green' ) themeColor = 'emerald';
    if ( color === 'red' ) themeColor = 'rose';
    if ( color === 'neutral' ) themeColor = 'slate';

    let glassClasses = '';

    if ( themeColor === 'emerald' )
    {
        glassClasses = 'bg-white/90 dark:bg-slate-900/80 backdrop-blur-xl ring-1 ring-inset ring-emerald-300/60 dark:ring-emerald-700/40 text-emerald-700 dark:text-emerald-400 shadow-[0_2px_8px_rgba(16,185,129,0.08),inset_0_1px_1px_rgba(255,255,255,0.9)] dark:shadow-[0_2px_8px_rgba(16,185,129,0.15),inset_0_1px_1px_rgba(255,255,255,0.05)] hover:shadow-[0_4px_12px_rgba(16,185,129,0.15)]';
    } else if ( themeColor === 'rose' )
    {
        glassClasses = 'bg-white/90 dark:bg-slate-900/80 backdrop-blur-xl ring-1 ring-inset ring-rose-300/60 dark:ring-rose-700/40 text-rose-700 dark:text-rose-400 shadow-[0_2px_8px_rgba(244,63,94,0.08),inset_0_1px_1px_rgba(255,255,255,0.9)] dark:shadow-[0_2px_8px_rgba(244,63,94,0.15),inset_0_1px_1px_rgba(255,255,255,0.05)] hover:shadow-[0_4px_12px_rgba(244,63,94,0.15)]';
    } else if ( themeColor === 'amber' )
    {
        glassClasses = 'bg-white/90 dark:bg-slate-900/80 backdrop-blur-xl ring-1 ring-inset ring-amber-300/60 dark:ring-amber-700/40 text-amber-700 dark:text-amber-400 shadow-[0_2px_8px_rgba(245,158,11,0.08),inset_0_1px_1px_rgba(255,255,255,0.9)] dark:shadow-[0_2px_8px_rgba(245,158,11,0.15),inset_0_1px_1px_rgba(255,255,255,0.05)] hover:shadow-[0_4px_12px_rgba(245,158,11,0.15)]';
    } else
    {
        // slate default
        glassClasses = 'bg-white/90 dark:bg-slate-900/80 backdrop-blur-xl ring-1 ring-inset ring-slate-300/60 dark:ring-slate-700/40 text-slate-500 dark:text-slate-400 shadow-[0_2px_8px_rgba(148,163,184,0.08),inset_0_1px_1px_rgba(255,255,255,0.9)] dark:shadow-[0_2px_8px_rgba(0,0,0,0.15),inset_0_1px_1px_rgba(255,255,255,0.05)] hover:shadow-[0_4px_12px_rgba(0,0,0,0.06)]';
    }

    const sizeClasses = size === 'sm' ? 'h-[24px] text-[11px] px-1.5 min-w-[50px]' : 'h-[30px] text-[13px] px-2 min-w-[70px]';

    return (
        <div className={ `relative inline-flex w-fit items-center justify-center rounded-md font-bold! tabular-nums tracking-tight transition-shadow duration-200 mx-auto ${sizeClasses} ${glassClasses} ${className}` }>
            { showArrow && (
                <span className="text-[10px] mr-1 opacity-80">
                    { isPos ? '▲' : isNeg ? '▼' : '—' }
                </span>
            ) }
            { prefix }
            { fastPathId ? (
                <span data-fast-path={fastPathId}>{value}</span>
            ) : (
                value
            ) }
            { suffix }
        </div>
    );
} );
